import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useLazyQuery } from "@apollo/client";
import Sentry from "../../api/sentry";

/* Redux State Subscriptions */
const useStateSubscription = (storeType, storeValue) => {
  return useSelector((state) => state[storeType][storeValue]);
};

const useApplicationState = (key) => {
  return useSelector((state) => state.application[key]);
};

const useCurrentStep = () => {
  return useApplicationState("currentStep");
};

/* React Router Dom helpers */
const useQueryParams = () => {
  const { search } = useLocation();
  const searchParams = search
    .slice(1)
    .split("&")
    .reduce((a, c) => ({ [c.split("=")[0]]: c.split("=")[1], ...a }), {});
  searchParams.get = (param) => {
    return searchParams[param];
  };

  return useMemo(() => searchParams, [searchParams]);
};

/* API Calls */
const usePostApi = (url) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async (payload) => {
    let res;
    let apiError;
    let customData;
    setLoading(true);

    try {
      res = await axios.post(url, payload);
    } catch (err) {
      apiError = err;
      Sentry.captureException(err);
      setError(err);
      throw err;
    } finally {
      customData = {
        ...res?.data,
        ...apiError?.data,
      };
      setData(customData);
      setLoading(false);
    }

    return customData;
  };

  return [fetchData, { loading, error, data }];
};

/* GQL Calls */
// Shame on Apollo for not returning a promise on useLazyQuery as promised in Docs...
// https://github.com/apollographql/apollo-client/issues/9354
const usePromisedLazyQuery = (QUERY, options) => {
  const [gqlFetch, { data, loading, error }] = useLazyQuery(QUERY, options);

  const promiseWrapper = (queryInstanceOptions) =>
    new Promise((res, rej) => {
      gqlFetch({
        ...queryInstanceOptions,
        onCompleted: res,
        onError: (err) => {
          err.statusCode = err?.networkError?.statusCode;
          Sentry.captureException(err);
          rej(err);
        },
      });
    });

  return [promiseWrapper, { data, loading, error }];
};

export {
  useStateSubscription,
  useApplicationState,
  useCurrentStep,
  useQueryParams,
  usePostApi,
  usePromisedLazyQuery,
};
